import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { postOverlay, scopeBody, num, str } from '@/lib/overlayApi';
import type { DateRange } from '@/lib/dateRange';

export interface MapCity {
  city: string;
  wtOsa: number | null;
  osaInLs: number | null;
  listing: number | null;
  sales: number;
  soh: number | null;
  skuCount: number | null;
  /** Null when the geo table has no position for this city yet. */
  lat: number | null;
  lon: number | null;
  storeCount: number | null;
}

/**
 * City metrics for the OSA map.
 *
 * Served only for a materialised window — the endpoint refuses the rest rather
 * than putting a 66 GB scan behind a glanceable view.
 */
export function useOsaMap(
  customerName: string,
  scope: { category?: string | undefined; city?: string | undefined; sku?: string | undefined },
  range: DateRange,
  enabled: boolean,
): UseQueryResult<MapCity[], Error> {
  return useQuery({
    queryKey: ['osa-map', customerName, scope, range],
    enabled,
    staleTime: 10 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const res = await postOverlay<Record<string, unknown>>(
        '/v1/availability/map',
        scopeBody({ customer_name: customerName, ...scope }, range),
        signal,
      );
      return res.rows.map((r): MapCity => ({
        city: str(r['city_name']),
        wtOsa: num(r['wt_osa']),
        osaInLs: num(r['osa_in_ls']),
        listing: num(r['listing']),
        sales: num(r['sales']) ?? 0,
        soh: num(r['soh']),
        skuCount: num(r['sku_count']),
        lat: num(r['lat']),
        lon: num(r['lon']),
        storeCount: num(r['store_count']),
      }));
    },
  });
}

/** Figma legend: High >= 75, Mid 65-74, Low < 65. */
export type OsaBand = 'high' | 'mid' | 'low' | 'none';
export function osaBand(v: number | null): OsaBand {
  if (v === null) return 'none';
  if (v >= 75) return 'high';
  if (v >= 65) return 'mid';
  return 'low';
}
